import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Add, FolderCopy, CreateNewFolder, Edit, Delete, UploadFile, Link as LinkIcon } from '@mui/icons-material';
import { useCreateFolder, useRenameFolder, useDeleteFolder } from '../../hooks/useFolders';
import ConfirmDialog from '../common/ConfirmDialog';
import ExportLinksDialog from '../common/ExportLinksDialog';
import FolderTree from './FolderTree';

export default function Sidebar({ selectedFolderId, onFolderSelect, folderTree, onUploadClick, onFileDrop }) {
  const [createDialog, setCreateDialog] = useState(false);
  const [createParentId, setCreateParentId] = useState(undefined);
  const [newFolderName, setNewFolderName] = useState('');
  const [createError, setCreateError] = useState('');
  const [expandedItems, setExpandedItems] = useState([]);

  // Rename state
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameFolder, setRenameFolder] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Export links state
  const [exportLinksParams, setExportLinksParams] = useState(null);
  const [exportLinksTitle, setExportLinksTitle] = useState('');

  const createFolderMut = useCreateFolder();
  const renameFolderMut = useRenameFolder();
  const deleteFolderMut = useDeleteFolder();

  const handleExpandedItemsChange = useCallback((ids) => {
    setExpandedItems(ids);
  }, []);

  // --- Create folder ---
  const openCreateDialog = (parentId) => {
    setCreateParentId(parentId);
    setNewFolderName('');
    setCreateError('');
    setCreateDialog(true);
  };

  const handleCreate = async () => {
    if (!newFolderName.trim()) return;
    setCreateError('');
    try {
      await createFolderMut.mutateAsync({
        name: newFolderName.trim(),
        parent: createParentId || null,
      });
      if (createParentId && !expandedItems.includes(createParentId)) {
        setExpandedItems((prev) => [...prev, createParentId]);
      }
      setNewFolderName('');
      setCreateDialog(false);
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.name?.[0] || data?.detail || 'Errore durante la creazione';
      setCreateError(msg);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialog(false);
    setCreateError('');
    setNewFolderName('');
  };

  // --- Rename folder ---
  const openRenameDialog = (folder) => {
    setRenameFolder(folder);
    setRenameName(folder.name);
    setRenameError('');
    setRenameDialog(true);
  };

  const handleRename = async () => {
    if (!renameName.trim() || !renameFolder) return;
    setRenameError('');
    try {
      await renameFolderMut.mutateAsync({ id: renameFolder.id, name: renameName.trim() });
      setRenameDialog(false);
      setRenameFolder(null);
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.name?.[0] || data?.detail || 'Errore durante la rinomina';
      setRenameError(msg);
    }
  };

  const handleCloseRenameDialog = () => {
    setRenameDialog(false);
    setRenameFolder(null);
    setRenameError('');
  };

  // --- Delete folder ---
  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteFolderMut.mutateAsync(confirmDelete.id);
      if (selectedFolderId === confirmDelete.id) {
        onFolderSelect(null);
      }
    } finally {
      setConfirmDelete(null);
    }
  };

  // --- Context menu ---
  const handleContextMenu = (event, folder) => {
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      folder, // null = root
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextNewSubfolder = () => {
    const parentId = contextMenu?.folder?.id || null;
    handleCloseContextMenu();
    openCreateDialog(parentId);
  };

  const handleContextRename = () => {
    const folder = contextMenu?.folder;
    handleCloseContextMenu();
    if (folder) openRenameDialog(folder);
  };

  const handleContextDelete = () => {
    const folder = contextMenu?.folder;
    handleCloseContextMenu();
    if (folder) setConfirmDelete(folder);
  };

  const handleContextUpload = () => {
    const folder = contextMenu?.folder;
    handleCloseContextMenu();
    // Select the folder first so uploads go there, then open file picker
    onFolderSelect(folder?.id || null);
    if (onUploadClick) onUploadClick();
  };

  const handleContextExportLinks = () => {
    const folder = contextMenu?.folder;
    handleCloseContextMenu();
    setExportLinksParams({ folderId: folder?.id || null });
    setExportLinksTitle(
      folder
        ? `Esporta link: ${folder.name} (incluse sottocartelle)`
        : 'Esporta link: tutti i file (root + sottocartelle)'
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FolderCopy color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          File Manager
        </Typography>
      </Box>
      <Divider />

      {/* New folder button */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Add />}
          onClick={() => openCreateDialog(selectedFolderId)}
          size="small"
        >
          Nuova cartella
        </Button>
      </Box>

      {/* Folder tree */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        <FolderTree
          tree={folderTree}
          selectedFolderId={selectedFolderId}
          onFolderSelect={onFolderSelect}
          expandedItems={expandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
          onContextMenu={handleContextMenu}
          onFileDrop={onFileDrop}
        />
      </Box>

      {/* Folder context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleContextNewSubfolder}>
          <ListItemIcon>
            <CreateNewFolder fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {contextMenu?.folder ? 'Nuova sottocartella' : 'Nuova cartella'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextUpload}>
          <ListItemIcon>
            <UploadFile fontSize="small" />
          </ListItemIcon>
          <ListItemText>Carica file</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextExportLinks}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {contextMenu?.folder
              ? 'Esporta link (incluse sottocartelle)'
              : 'Esporta tutti i link'}
          </ListItemText>
        </MenuItem>
        {contextMenu?.folder && (
          <MenuItem onClick={handleContextRename}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rinomina</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.folder && (
          <MenuItem onClick={handleContextDelete}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Elimina</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create folder dialog */}
      <Dialog open={createDialog} onClose={handleCloseCreateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Nuova cartella</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {createError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Nome cartella"
            value={newFolderName}
            onChange={(e) => { setNewFolderName(e.target.value); setCreateError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
            disabled={createFolderMut.isPending}
            error={Boolean(createError)}
          />
          {createParentId && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Verrà creata come sottocartella della cartella selezionata
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annulla</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newFolderName.trim() || createFolderMut.isPending}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={renameDialog} onClose={handleCloseRenameDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Rinomina cartella</DialogTitle>
        <DialogContent>
          {renameError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {renameError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Nome cartella"
            value={renameName}
            onChange={(e) => { setRenameName(e.target.value); setRenameError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            sx={{ mt: 1 }}
            disabled={renameFolderMut.isPending}
            error={Boolean(renameError)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog}>Annulla</Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameName.trim() || renameFolderMut.isPending}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete folder confirm */}
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Elimina cartella"
        message={`Sei sicuro di voler eliminare la cartella "${confirmDelete?.name}"? Tutti i file e le sottocartelle verranno eliminati.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        loading={deleteFolderMut.isPending}
      />

      {/* Export links dialog */}
      <ExportLinksDialog
        open={Boolean(exportLinksParams)}
        onClose={() => setExportLinksParams(null)}
        params={exportLinksParams}
        title={exportLinksTitle}
      />
    </Box>
  );
}
